// Day-grouping for the message thread: insert a divider whenever the local
// calendar day changes between consecutive messages. Pure + `now`-injectable
// so callers and tests get deterministic output (mirrors formatRelativeTime).

function toDate(input: string | number | Date): Date {
  return input instanceof Date ? input : new Date(input);
}

// Stable key for a local calendar day. Invalid timestamps collapse to one
// "invalid" bucket rather than throwing.
export function dayKey(input: string | number | Date): string {
  const d = toDate(input);
  if (Number.isNaN(d.getTime())) return 'invalid';
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

// Human label for a day divider: "Today" / "Yesterday" / a localized date
// (weekday for the current year, year-qualified otherwise).
export function dayDividerLabel(
  input: string | number | Date,
  now: number = Date.now(),
): string {
  const d = toDate(input);
  if (Number.isNaN(d.getTime())) return 'Unknown date';

  const today = new Date(now);
  if (dayKey(d) === dayKey(today)) return 'Today';

  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (dayKey(d) === dayKey(yesterday)) return 'Yesterday';

  const sameYear = d.getFullYear() === today.getFullYear();
  return d.toLocaleDateString(
    undefined,
    sameYear
      ? { weekday: 'short', month: 'short', day: 'numeric' }
      : { month: 'short', day: 'numeric', year: 'numeric' },
  );
}

export interface DayDivider {
  kind: 'divider';
  id: string;
  label: string;
}

export interface GroupedMessage<T> {
  kind: 'message';
  message: T;
}

export type MessageGroupEntry<T> = DayDivider | GroupedMessage<T>;

// Interleave day dividers with messages. Assumes `messages` is in display
// order (oldest → newest); a divider precedes the first message of each day.
export function groupMessagesByDay<
  T extends { message_id: string; sent_at: string },
>(messages: T[], now: number = Date.now()): MessageGroupEntry<T>[] {
  const out: MessageGroupEntry<T>[] = [];
  let lastKey: string | null = null;
  for (const message of messages) {
    const key = dayKey(message.sent_at);
    if (key !== lastKey) {
      out.push({
        kind: 'divider',
        id: `divider-${message.message_id}`,
        label: dayDividerLabel(message.sent_at, now),
      });
      lastKey = key;
    }
    out.push({ kind: 'message', message });
  }
  return out;
}
